import { useState } from "react"
import { useNavigate } from "react-router-dom"
import AppShell from "@/components/AppShell/AppShell"
import Button from "@/components/Button/Button"
import DataTable from "@/components/DataTable/DataTable"
import type { Column } from "@/components/DataTable/DataTable"
import FilterPills from "@/components/FilterPills/FilterPills"
import SessionRemoveDialog from "@/components/RemoveDialog/SessionRemoveDialog"
import { ACTION_GLYPH, BinGlyph, BoxGlyph } from "@/components/RemoveDialog/removeGlyphs"
import StatusDot from "@/components/StatusDot/StatusDot"
import StatusScreen from "@/components/StatusScreen/StatusScreen"
import Tooltip from "@/components/Tooltip/Tooltip"
import { removeActionLabel } from "@/content/archive"
import { UNTITLED_SESSION } from "@/content/customers"
import {
  SESSIONS_BLURB,
  SESSIONS_EMPTY,
  SESSIONS_TITLE,
  TABLE_CUSTOMER,
  TABLE_DATE,
  TABLE_SESSION,
  TABLE_STATUS,
  TABLE_VIEW,
} from "@/content/nav"
import { matchesFilter } from "@/content/status"
import type { StateFilter } from "@/content/status"
import { useExpiryTick } from "@/hooks/useExpiryTick"
import { invalidateFacilitatorIndex } from "@/hooks/useFacilitatorIndex"
import type { FacilitatorIndex } from "@/hooks/useFacilitatorIndex"
import { useFirestoreSubmit } from "@/hooks/useFirestoreWrite"
import { removeSession } from "@/services/sessions"
import { useAuthStore, selectAuthUid } from "@/store/authStore"
import { customerDetailPath, sessionPath } from "@/utils/routePaths"
import { shortDate } from "@/utils/relativeTime"
import { sessionState } from "@/utils/sessionLifecycle"
import type { RemovalKind, Session } from "@/types"
import styles from "@/styles/pages/IndexPage.module.css"

/**
 * Every session the signed-in facilitator may open, newest first.
 *
 * Built on the lists `AppShell` already holds, which come from
 * `listSessionsForFacilitator` — the one `array-contains facilitatorIds` query
 * the rules permit. No new service function, no rules change, and no `orderBy`
 * or `limit()` on a server-stamped field.
 */
export default function SessionsList() {
  return <AppShell>{(index) => <SessionsScreen index={index} />}</AppShell>
}

function SessionsScreen({ index }: { index: FacilitatorIndex }) {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<StateFilter>("all")
  /* Which session the dialog is about, and which way it is going. One variable,
     because the two controls open the same dialog with different copy. */
  const [removing, setRemoving] = useState<{ session: Session; kind: RemovalKind } | null>(null)
  const uid = useAuthStore(selectAuthUid)
  const removal = useFirestoreSubmit<boolean>()
  const { sessions, error, loading, reload } = index
  // These lists are read once. Without this, a row that expires while the tab
  // is open keeps saying Live until something unrelated re-renders it.
  const now = useExpiryTick((sessions ?? []).map((session) => session.expiresAt))

  if (error !== null && sessions === null) {
    return (
      <StatusScreen
        nested
        eyebrow={SESSIONS_TITLE}
        title="The session list did not load."
        raw={error}
      >
        <Button loading={loading} onClick={reload}>
          Try again
        </Button>
      </StatusScreen>
    )
  }

  if (sessions === null) {
    return <StatusScreen nested eyebrow={SESSIONS_TITLE} title="Loading sessions." waiting />
  }

  const shown = sessions.filter((session) => matchesFilter(sessionState(session, now), filter))

  const columns: readonly Column<Session>[] = [
    {
      key: "session",
      header: TABLE_SESSION,
      width: "30%",
      render: (session) => {
        const label = session.name || UNTITLED_SESSION
        return (
          <span className={styles.nameCell}>
            <span className={styles.strong}>{label}</span>
            <span className={styles.rowTools}>
              <Tooltip label={removeActionLabel("archived", label)} placement="top">
                <button
                  type="button"
                  className={styles.iconLink}
                  aria-label={removeActionLabel("archived", label)}
                  onClick={() => setRemoving({ session, kind: "archived" })}
                >
                  <BoxGlyph size={ACTION_GLYPH} />
                </button>
              </Tooltip>
              <Tooltip label={removeActionLabel("deleted", label)} placement="top">
                <button
                  type="button"
                  className={`${styles.iconLink} ${styles.iconDelete}`}
                  aria-label={removeActionLabel("deleted", label)}
                  onClick={() => setRemoving({ session, kind: "deleted" })}
                >
                  <BinGlyph size={ACTION_GLYPH} />
                </button>
              </Tooltip>
            </span>
          </span>
        )
      },
    },
    {
      key: "customer",
      header: TABLE_CUSTOMER,
      width: "24%",
      render: (session) => (
        <button
          type="button"
          className={styles.link}
          onClick={() => navigate(customerDetailPath(session.customerId))}
        >
          {session.customerName || "Untitled customer"}
        </button>
      ),
    },
    {
      key: "date",
      header: TABLE_DATE,
      width: "16%",
      render: (session) => shortDate(session.createdAt),
    },
    {
      key: "status",
      header: TABLE_STATUS,
      width: "14%",
      render: (session) => <StatusDot state={sessionState(session, now)} compact />,
    },
    {
      key: "action",
      header: "",
      align: "right",
      render: (session) => (
        <span className={styles.rowActions}>
          <button
            type="button"
            className={styles.link}
            onClick={() => navigate(sessionPath(session.id))}
          >
            {TABLE_VIEW}
          </button>
        </span>
      ),
    },
  ]

  /*
   * The row this screen renders is the one the write changes, so it has to read
   * again rather than only marking the index stale — `invalidateFacilitatorIndex`
   * would leave the removed session in this table until the next navigation.
   */
  const applyRemoval = async () => {
    if (!removing || !uid) return
    const { session, kind } = removing
    const ok = await removal.submit(async () => {
      await removeSession(session.id, uid, kind)
      return true
    })
    if (ok) {
      setRemoving(null)
      invalidateFacilitatorIndex()
      index.reload()
    }
  }

  return (
    <>
      <header className={styles.head}>
        <h1 className={styles.title}>{SESSIONS_TITLE}</h1>
        <p className={styles.blurb}>{SESSIONS_BLURB}</p>
      </header>

      <div className={styles.controls}>
        <FilterPills value={filter} onChange={setFilter} />
      </div>

      <DataTable
        caption={SESSIONS_BLURB}
        columns={columns}
        rows={shown}
        rowKey={(session) => session.id}
        empty={
          <div className={styles.empty}>
            {sessions.length === 0 ? SESSIONS_EMPTY : "Nothing matches that filter."}
          </div>
        }
      />

      {removing !== null ? (
        <SessionRemoveDialog
          session={removing.session}
          kind={removing.kind}
          error={removal.error}
          pending={removal.pending}
          onClose={() => {
            setRemoving(null)
            removal.clearError()
          }}
          onConfirm={() => void applyRemoval()}
        />
      ) : null}
    </>
  )
}
